import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  addressMsg,
  availabilityResultsMsg,
  changeMsg,
  fixedBroadBandNettiLiteOffersMsg,
  fixedBroadBandNettiLiteParagraphMsg,
  mobileBroadbandNettiLiteOffersMsg,
  mobileBroadbandNettiLiteOffersParagraphMsg,
  nettiLiteWeRecommendMsg,
  t,
} from '../../common/i18n/index.js';
import { getCommercialProductPrices } from '../../common/utils/commercialProductUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { SalesType } from '../../generated/api/models.js';
import { Anchor } from '../Anchor/Anchor.js';
import { Grid } from '../Grid/Grid';
import { HorizontalFixedBroadbandSubscriptions } from '../HorizontalSubscriptionCard/HorizontalFixedBroadbandSubscriptions';
import { getOfferCardContent } from '../OrderSubscriptionSelection/CampaignProductCard.js';
import { Voucher } from '../OrderSubscriptionSelection/Voucher.js';
import {
  formatNettiliteOfferCardName,
  nettiLiteFixedBroadbandContent,
  nettiLiteMobileBroadbandContent,
  resolveBullets,
  resolveServices,
  resolveSpecifications,
} from '../OrderSubscriptionSelection/content/NettiLiteOfferSpecification.js';
import { ContactSupportBlock } from './ContactSupportBlock.js';
import { CoverageDisclaimer } from './CoverageDisclaimer.js';
import type { OnlineModel, Subscription } from '../../generated/api/models.js';
import type { State } from '../../selfservice/common/store.js';
import type { OfferCard } from '../OrderSubscriptionSelection/OrderSubscriptionSelection.js';
import type { SubCardProps } from '../SubscriptionCard/SubscriptionCard.js';

import '../OrderSubscriptionSelection/OrderSubscriptionSelection.scss';
import './NettiLiteOffers.scss';

export interface NettiLiteOffersProps {
  subscription?: Subscription | null;
  isBreadCrumbEnabled?: boolean;
  fixedBroadBandOnlineModel: OnlineModel;
  mobileBroadBandOnlineModel: OnlineModel;
  availabilityAddress: string;
  postalCode?: string;
}

// TODO we might benefit from doing this mapping at a higher level and without mapping it first to offercard as it is now a redundant step
const mapToSubscriptionCard = (offerCards: OfferCard[]): SubCardProps[] => {
  return offerCards.map(card => {
    const specifications = resolveSpecifications(card.offer.offerCode);

    const prices = getCommercialProductPrices(
      card.offer.commercialProducts[0],
      card.campaignAssociation,
      card.campaignAssociation?.salesType
    );
    return {
      onlineModel: card.onlineModel,
      offer: card.offer,
      bullets: resolveBullets(card.offer.offerCode),
      name: formatNettiliteOfferCardName(card.offer.offerCode, card.offer.offerName),
      description: card.content.body,
      mainHeader: card?.content?.title || '',
      oneTimePrice: prices.oneTimeCharge,
      originalOneTimePrice: prices.oneTimeChargeBeforeDiscount,
      monthlyPrice: prices.monthlyRecurringCharge,
      originalMonthlyPrice: prices.monthlyRecurringChargeBeforeDiscount,
      fixedTermMonths: card.campaignAssociation?.fixedTermPeriod,
      voucher: card.voucher,
      pricePreamble: t.OD1L('From'),
      showSticker: false,
      selectedOffer: card.offer.offerCode,
      services: resolveServices(card.offer.offerCode),
      specifications: specifications,
      voucherCode: card.voucher,
    };
  });
};

export const NettiLiteOffers = (props: NettiLiteOffersProps) => {
  const { fixedBroadBandOnlineModel, mobileBroadBandOnlineModel, availabilityAddress } = props;
  const [showAllFixedBbOffers, setShowAllFixedBbOffers] = useState<boolean>(false);
  const [showAllMobileBbOffers, setShowAllMobileBbOffers] = useState<boolean>(false);

  const campaigns = useSelector((state: State) => state.campaigns, deepEqual);
  const campaignPromotionsFromVoucher = campaigns?.campaigns || [];
  const campaignContextsFromVoucher = campaigns?.campaignContexts || [];

  const fixedBroadbandCampaignPromotions = [
    ...campaignPromotionsFromVoucher,
    ...(fixedBroadBandOnlineModel.campaigns || []),
  ];
  const mobileBroadbandCampaignPromotions = [
    ...campaignPromotionsFromVoucher,
    ...(mobileBroadBandOnlineModel.campaigns || []),
  ];

  const offerCardContentsForFixedBroadband = getOfferCardContent(
    fixedBroadBandOnlineModel,
    undefined,
    false,
    { campaignContexts: campaignContextsFromVoucher, voucher: campaigns?.voucher },
    true,
    fixedBroadbandCampaignPromotions,
    nettiLiteFixedBroadbandContent()
  );

  const offerCardContentsForMobileBroadband = getOfferCardContent(
    mobileBroadBandOnlineModel,
    undefined,
    false,
    { campaignContexts: campaignContextsFromVoucher, voucher: campaigns?.voucher },
    true,
    mobileBroadbandCampaignPromotions,
    nettiLiteMobileBroadbandContent()
  );

  return (
    <CL.Grid id="of-order-subscription-selection" className="of-order-subscription-selection of-nettilite-container">
      <CL.GridRow className="of-change-availablity-address-row">
        <CL.GridCol colWidth={12}>
          <div>
            <h1>{t.IGS3(availabilityResultsMsg)}</h1>
          </div>
        </CL.GridCol>
        <CL.GridCol colWidth={12}>
          <div className="ds-text--bold ds-padding--4">
            {`${t.GWZ6(addressMsg)}: ${availabilityAddress}`}{' '}
            <Anchor className="of-change-address-link" onClick={() => history.back()}>
              {t.SEER(changeMsg)}
            </Anchor>
          </div>
        </CL.GridCol>
        <CL.GridCol className="title-voucher-row" colWidth={12} colWidthM={6} colWidthL={6}>
          <Voucher onlineModelCode={fixedBroadBandOnlineModel.onlineModelCode} salesType={SalesType.NEW_SALE} />
        </CL.GridCol>
      </CL.GridRow>

      <CL.GridRow>
        <CL.GridCol colWidth={12}>
          {offerCardContentsForFixedBroadband.length > 0 && (
            <>
              <h2>{t.PKML(fixedBroadBandNettiLiteOffersMsg)}</h2>
              <p>{t.WQ2D(fixedBroadBandNettiLiteParagraphMsg)}</p>
            </>
          )}
        </CL.GridCol>
      </CL.GridRow>
      {offerCardContentsForFixedBroadband.length > 0 && (
        <CL.GridRow>
          <CL.GridCol colWidth={12}>
            <h3>{t.E82U(nettiLiteWeRecommendMsg)} </h3>
          </CL.GridCol>
          <CL.GridCol colWidth={12}>
            <Grid>
              <HorizontalFixedBroadbandSubscriptions
                subscriptions={mapToSubscriptionCard(offerCardContentsForFixedBroadband.slice(0, 3))}
                isMobileProduct={false}
                {...props}
              />
            </Grid>

            {offerCardContentsForFixedBroadband.length > 3 && (
              <div>
                <CL.Button
                  color="link"
                  onClick={() => setShowAllFixedBbOffers(!showAllFixedBbOffers)}
                  className="nettilite-text-button"
                >
                  <CL.Icon icon={showAllFixedBbOffers ? 'arrow-up' : 'arrow-down'} size="l" />{' '}
                  {t.Z7TJ('Show more options for fixed-line Netti Lite subscriptions')}
                </CL.Button>
              </div>
            )}
            {showAllFixedBbOffers && (
              <Grid>
                <HorizontalFixedBroadbandSubscriptions
                  isMobileProduct={false}
                  subscriptions={mapToSubscriptionCard(offerCardContentsForFixedBroadband.slice(3))}
                  {...props}
                />
              </Grid>
            )}
          </CL.GridCol>
        </CL.GridRow>
      )}
      <CL.GridRow>
        <CL.GridCol colWidth={12} className="heading-margin">
          <h2>{t.A575(mobileBroadbandNettiLiteOffersMsg)}</h2>
          <p>{t.WQ2E(mobileBroadbandNettiLiteOffersParagraphMsg)}</p>
          <CoverageDisclaimer />
        </CL.GridCol>
      </CL.GridRow>

      <CL.GridRow>
        <CL.GridCol colWidth={12}>
          <h3>{t.E82U(nettiLiteWeRecommendMsg)} </h3>
        </CL.GridCol>
        <CL.GridCol colWidth={12}>
          <Grid>
            <HorizontalFixedBroadbandSubscriptions
              subscriptions={mapToSubscriptionCard(offerCardContentsForMobileBroadband.slice(0, 3))}
              isMobileProduct={true}
              {...props}
            />
          </Grid>

          {offerCardContentsForMobileBroadband.length > 3 && (
            <div>
              <CL.Button
                color="link"
                onClick={() => setShowAllMobileBbOffers(!showAllMobileBbOffers)}
                className="nettilite-text-button"
              >
                <CL.Icon icon={showAllMobileBbOffers ? 'arrow-up' : 'arrow-down'} size="l" />{' '}
                {t.TF8Q('Show more options for mobile network Netti Lite subscriptions')}
              </CL.Button>
            </div>
          )}
          {showAllMobileBbOffers && (
            <Grid>
              <HorizontalFixedBroadbandSubscriptions
                subscriptions={mapToSubscriptionCard(offerCardContentsForMobileBroadband.slice(3))}
                isMobileProduct={true}
                {...props}
              />
            </Grid>
          )}
        </CL.GridCol>
      </CL.GridRow>

      <CL.GridRow className="of-contact-support">
        <CL.GridCol colWidth={12}>
          <ContactSupportBlock />
        </CL.GridCol>
      </CL.GridRow>
    </CL.Grid>
  );
};
